import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  ConstructionCompanyRegistrationForm,
  CustomerRegistrationForm,
  SupplierRegistrationForm,
  WitnessCompanyRegistrationForm,
  FormValidationState,
  NoharaRepresentative,
  Address,
  HeadOfficeAddress,
} from '../types/company.types';

// Shared default values to avoid duplication
const defaultCurrentAddress: Address = {
  postalCode1: '',
  postalCode2: '',
  address1: '',
  address2: '',
  address3: '',
  phone: '',
  fax: '',
};

const defaultHeadOfficeAddress: HeadOfficeAddress = {
  postalCode1: '',
  postalCode2: '',
  address1: '',
  address2: '',
  address3: '',
};

const createDefaultRepresentative = (): NoharaRepresentative => ({
  id: `rep-init-${globalThis.crypto.randomUUID()}`,
  department: '',
  salesPerson: {
    id: '',
    lastName: '',
    firstName: '',
    code: '',
  },
});

const defaultIsCommonCement = false;

// Initial form data for each company type
export const initialConstructionFormData: ConstructionCompanyRegistrationForm = {
  galiCustomerCode: '',
  officialName: '',
  abbreviatedName: '',
  nameKatakana: '',
  representatives: [createDefaultRepresentative()] as NoharaRepresentative[],
};

export const initialCustomerFormData: CustomerRegistrationForm = {
  companyType: '得意先',
  galiCode: '',
  galiCustomerCode: '',
  officialName: '',
  abbreviatedName: '',
  branchDepartment: '',
  nameKatakana: '',
  customerNameKatakana: '',
  customerCategory: undefined as number | undefined,
  currentAddress: defaultCurrentAddress,
  headOfficeAddress: defaultHeadOfficeAddress,
  representatives: [createDefaultRepresentative()],
  isCommonCement: defaultIsCommonCement,
};

export const initialWitnessCompanyFormData: WitnessCompanyRegistrationForm = {
  companyType: '立会会社',
  officialName: '',
  tel: '',
  fax: '',
  contactPerson: '',
  contactTel: '',
  representatives: [createDefaultRepresentative()],
  isCommonCement: defaultIsCommonCement,
};

export const initialSupplierFormData: SupplierRegistrationForm = {
  companyType: '仕入先',
  galiCode: '',
  officialName: '',
  abbreviatedName: '',
  branchDepartment: '',
  nameKatakana: '',
  currentAddress: defaultCurrentAddress,
  headOfficeAddress: defaultHeadOfficeAddress,
  representatives: [createDefaultRepresentative()],
  isCommonCement: defaultIsCommonCement,
  supplierType: '組合',
  galiSupplierCode: '',
  supplierNameKatakana: '',
  supplierCategory: 'ゼネコン',
  factoryAddress: {
    factoryName: '',
    postalCode1: '',
    postalCode2: '',
    addressLocation: '',
  },
  factoryContacts: [],
  certificationInfo: {
    jisNumber: '',
    certificationType: '',
    highStrengthNumber: '',
  },
  unionMembership: {
    industrialUnion: '',
    cooperativeUnions: [],
  },
  qualifiedPersonnel: {
    professionalEngineers: '',
    concreteDiagnosticians: '',
    seniorConcreteTechnicians: '',
    concreteTechnicians: '',
  },
  rawMaterials: {
    cement: [],
    coarseAggregate: [],
    fineAggregate: [],
  },
  mixerInfo: {
    ratedCapacity: '',
    type: '',
    mixerCapacity: '',
    mixerCardinality: '',
    manufacturer: '',
    measurementMethod: '',
  },
  transportVehicles: {
    large10t: '',
    medium8t: '',
    size7t: '',
    size5t: '',
    small4t: '',
    size3t: '',
    size2t: '',
    other: '',
  },
};

// Store interface
interface CompanyRegistrationStore {
  // Form data for each company type
  constructionFormData: ConstructionCompanyRegistrationForm;
  customerFormData: CustomerRegistrationForm;
  supplierFormData: SupplierRegistrationForm;
  witnessCompanyFormData: WitnessCompanyRegistrationForm;

  // Validation states
  constructionValidation: FormValidationState;
  customerValidation: FormValidationState;
  supplierValidation: FormValidationState;
  witnessCompanyValidation: FormValidationState;

  // Touched fields for each form
  constructionTouchedFields: Set<string>;
  customerTouchedFields: Set<string>;
  supplierTouchedFields: Set<string>;
  witnessCompanyTouchedFields: Set<string>;

  // Actions for Construction Company
  updateConstructionForm: (data: Partial<ConstructionCompanyRegistrationForm>) => void;
  resetConstructionForm: () => void;
  setConstructionValidation: (validation: FormValidationState) => void;
  setConstructionTouchedFields: (touchedFields: Set<string>) => void;

  // Actions for Customer
  updateCustomerForm: (data: Partial<CustomerRegistrationForm>) => void;
  resetCustomerForm: () => void;
  setCustomerValidation: (validation: FormValidationState) => void;
  setCustomerTouchedFields: (touchedFields: Set<string>) => void;

  // Actions for Supplier
  updateSupplierForm: (data: Partial<SupplierRegistrationForm>) => void;
  resetSupplierForm: () => void;
  setSupplierValidation: (validation: FormValidationState) => void;
  setSupplierTouchedFields: (touchedFields: Set<string>) => void;

  // Actions for Witness Company
  updateWitnessCompanyForm: (data: Partial<WitnessCompanyRegistrationForm>) => void;
  resetWitnessCompanyForm: () => void;
  setWitnessCompanyValidation: (validation: FormValidationState) => void;
  setWitnessCompanyTouchedFields: (touchedFields: Set<string>) => void;

  // Global actions
  resetAllForms: () => void;
  clearAllData: () => void;
}

// Create the store
export const useCompanyRegistrationStore = create<CompanyRegistrationStore>()(
  devtools(
    (set) => ({
      // Initial state
      constructionFormData: initialConstructionFormData,
      customerFormData: initialCustomerFormData,
      supplierFormData: initialSupplierFormData,
      witnessCompanyFormData: initialWitnessCompanyFormData,

      constructionValidation: {
        errors: [],
        isValid: false,
        isSubmitting: false,
      },
      customerValidation: {
        errors: [],
        isValid: false,
        isSubmitting: false,
      },
      supplierValidation: {
        errors: [],
        isValid: false,
        isSubmitting: false,
      },
      witnessCompanyValidation: {
        errors: [],
        isValid: false,
        isSubmitting: false,
      },

      constructionTouchedFields: new Set<string>(),
      customerTouchedFields: new Set<string>(),
      supplierTouchedFields: new Set<string>(),
      witnessCompanyTouchedFields: new Set<string>(),

      // Construction Company actions
      updateConstructionForm: (data) =>
        set(
          (state) => ({
            constructionFormData: { ...state.constructionFormData, ...data },
          }),
          false,
          'updateConstructionForm'
        ),

      resetConstructionForm: () =>
        set({ constructionFormData: initialConstructionFormData }, false, 'resetConstructionForm'),

      setConstructionValidation: (validation) =>
        set({ constructionValidation: validation }, false, 'setConstructionValidation'),

      setConstructionTouchedFields: (touchedFields) =>
        set({ constructionTouchedFields: touchedFields }, false, 'setConstructionTouchedFields'),

      // Customer actions
      updateCustomerForm: (data) =>
        set(
          (state) => ({
            customerFormData: { ...state.customerFormData, ...data },
          }),
          false,
          'updateCustomerForm'
        ),

      resetCustomerForm: () =>
        set({ customerFormData: initialCustomerFormData }, false, 'resetCustomerForm'),

      setCustomerValidation: (validation) =>
        set({ customerValidation: validation }, false, 'setCustomerValidation'),

      setCustomerTouchedFields: (touchedFields) =>
        set({ customerTouchedFields: touchedFields }, false, 'setCustomerTouchedFields'),

      // Supplier actions
      updateSupplierForm: (data) =>
        set(
          (state) => ({
            supplierFormData: { ...state.supplierFormData, ...data },
          }),
          false,
          'updateSupplierForm'
        ),

      resetSupplierForm: () =>
        set({ supplierFormData: initialSupplierFormData }, false, 'resetSupplierForm'),

      setSupplierValidation: (validation) =>
        set({ supplierValidation: validation }, false, 'setSupplierValidation'),

      setSupplierTouchedFields: (touchedFields) =>
        set({ supplierTouchedFields: touchedFields }, false, 'setSupplierTouchedFields'),

      // Witness Company actions
      updateWitnessCompanyForm: (data) =>
        set(
          (state) => ({
            witnessCompanyFormData: { ...state.witnessCompanyFormData, ...data },
          }),
          false,
          'updateWitnessCompanyForm'
        ),

      resetWitnessCompanyForm: () =>
        set(
          { witnessCompanyFormData: initialWitnessCompanyFormData },
          false,
          'resetWitnessCompanyForm'
        ),

      setWitnessCompanyValidation: (validation) =>
        set({ witnessCompanyValidation: validation }, false, 'setWitnessCompanyValidation'),

      setWitnessCompanyTouchedFields: (touchedFields) =>
        set(
          { witnessCompanyTouchedFields: touchedFields },
          false,
          'setWitnessCompanyTouchedFields'
        ),

      // Global actions
      resetAllForms: () =>
        set(
          {
            constructionFormData: initialConstructionFormData,
            customerFormData: initialCustomerFormData,
            supplierFormData: initialSupplierFormData,
            witnessCompanyFormData: initialWitnessCompanyFormData,
            constructionValidation: { errors: [], isValid: false, isSubmitting: false },
            customerValidation: { errors: [], isValid: false, isSubmitting: false },
            supplierValidation: { errors: [], isValid: false, isSubmitting: false },
            witnessCompanyValidation: { errors: [], isValid: false, isSubmitting: false },
            constructionTouchedFields: new Set<string>(),
            customerTouchedFields: new Set<string>(),
            supplierTouchedFields: new Set<string>(),
            witnessCompanyTouchedFields: new Set<string>(),
          },
          false,
          'resetAllForms'
        ),

      clearAllData: () =>
        set(
          {
            constructionFormData: initialConstructionFormData,
            customerFormData: initialCustomerFormData,
            supplierFormData: initialSupplierFormData,
            witnessCompanyFormData: initialWitnessCompanyFormData,
            constructionValidation: { errors: [], isValid: false, isSubmitting: false },
            customerValidation: { errors: [], isValid: false, isSubmitting: false },
            supplierValidation: { errors: [], isValid: false, isSubmitting: false },
            witnessCompanyValidation: { errors: [], isValid: false, isSubmitting: false },
            constructionTouchedFields: new Set<string>(),
            customerTouchedFields: new Set<string>(),
            supplierTouchedFields: new Set<string>(),
            witnessCompanyTouchedFields: new Set<string>(),
          },
          false,
          'clearAllData'
        ),
    }),
    {
      name: 'company-registration-store',
    }
  )
);

// Selectors for better performance
export const useConstructionFormData = () =>
  useCompanyRegistrationStore((state) => state.constructionFormData);

export const useCustomerFormData = () =>
  useCompanyRegistrationStore((state) => state.customerFormData);

export const useSupplierFormData = () =>
  useCompanyRegistrationStore((state) => state.supplierFormData);

export const useConstructionValidation = () =>
  useCompanyRegistrationStore((state) => state.constructionValidation);

export const useCustomerValidation = () =>
  useCompanyRegistrationStore((state) => state.customerValidation);

export const useSupplierValidation = () =>
  useCompanyRegistrationStore((state) => state.supplierValidation);

export const useWitnessCompanyFormData = () =>
  useCompanyRegistrationStore((state) => state.witnessCompanyFormData);

export const useWitnessCompanyValidation = () =>
  useCompanyRegistrationStore((state) => state.witnessCompanyValidation);

export const useWitnessCompanyTouchedFields = () =>
  useCompanyRegistrationStore((state) => state.witnessCompanyTouchedFields);

export const useConstructionTouchedFields = () =>
  useCompanyRegistrationStore((state) => state.constructionTouchedFields);

export const useCustomerTouchedFields = () =>
  useCompanyRegistrationStore((state) => state.customerTouchedFields);

export const useSupplierTouchedFields = () =>
  useCompanyRegistrationStore((state) => state.supplierTouchedFields);

// Action selectors - separate functions to avoid object recreation
export const useConstructionActions = () => {
  const updateForm = useCompanyRegistrationStore((state) => state.updateConstructionForm);
  const resetForm = useCompanyRegistrationStore((state) => state.resetConstructionForm);
  const setValidation = useCompanyRegistrationStore((state) => state.setConstructionValidation);
  const setTouchedFields = useCompanyRegistrationStore(
    (state) => state.setConstructionTouchedFields
  );

  return { updateForm, resetForm, setValidation, setTouchedFields };
};

export const useCustomerActions = () => {
  const updateForm = useCompanyRegistrationStore((state) => state.updateCustomerForm);
  const resetForm = useCompanyRegistrationStore((state) => state.resetCustomerForm);
  const setValidation = useCompanyRegistrationStore((state) => state.setCustomerValidation);
  const setTouchedFields = useCompanyRegistrationStore((state) => state.setCustomerTouchedFields);

  return { updateForm, resetForm, setValidation, setTouchedFields };
};

export const useSupplierActions = () => {
  const updateForm = useCompanyRegistrationStore((state) => state.updateSupplierForm);
  const resetForm = useCompanyRegistrationStore((state) => state.resetSupplierForm);
  const setValidation = useCompanyRegistrationStore((state) => state.setSupplierValidation);
  const setTouchedFields = useCompanyRegistrationStore((state) => state.setSupplierTouchedFields);

  return { updateForm, resetForm, setValidation, setTouchedFields };
};

export const useWitnessCompanyActions = () => {
  const updateForm = useCompanyRegistrationStore((state) => state.updateWitnessCompanyForm);
  const resetForm = useCompanyRegistrationStore((state) => state.resetWitnessCompanyForm);
  const setValidation = useCompanyRegistrationStore((state) => state.setWitnessCompanyValidation);
  const setTouchedFields = useCompanyRegistrationStore(
    (state) => state.setWitnessCompanyTouchedFields
  );

  return { updateForm, resetForm, setValidation, setTouchedFields };
};

export const useGlobalActions = () => {
  const resetAllForms = useCompanyRegistrationStore((state) => state.resetAllForms);
  const clearAllData = useCompanyRegistrationStore((state) => state.clearAllData);

  return { resetAllForms, clearAllData };
};

// Note: useHasUnsavedChanges removed - use useTabUnsavedChanges instead for tab-specific checking
